/**
 * Where is this organisation based?
 *
 * Planning Center supplies an organisation's country and timezone when it is
 * created. Nothing supplies them for a standalone organisation, and there was
 * no way to enter them — Organization Settings only ever displayed them. The
 * gap is not cosmetic: per-message cost estimates resolve a pricing rate by
 * matching the organisation's country, so without one they come back blank.
 *
 * Only standalone organisations see this step.
 */

import { Component, OnInit, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import { OnboardingService } from '../../core/services/onboarding.service';
import { COUNTRIES, Country, DEFAULT_COUNTRY_ISO, countryByIso, flagFor } from '../../core/config/countries';

@Component({
    selector: 'app-location-setup',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './location-setup.component.html'
})
export class LocationSetupComponent implements OnInit {

    private auth = inject(Auth);
    private authState$ = authState(this.auth);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);
    private onboardingService = inject(OnboardingService);
    private router = inject(Router);

    readonly countries = COUNTRIES;
    readonly flagFor = flagFor;

    organizationId = '';
    organizationName = '';
    currentStep = 1;
    totalSteps = 3;
    progressPercentage = 0;

    countryIso = DEFAULT_COUNTRY_ISO;
    timezone = '';
    isSaving = false;
    isLoading = true;

    /** Timezones the browser knows about, for the picker. */
    timezones: string[] = [];

    async ngOnInit(): Promise<void> {
        const user = await firstValueFrom(this.authState$);
        if (!user) {
            this.router.navigate(['/auth/login']);
            return;
        }

        const setup = await this.onboardingService.getSetupContext(user.uid, 'location-setup');
        this.organizationId = setup.organizationId;
        this.organizationName = setup.organizationName;
        this.currentStep = setup.currentStep;
        this.totalSteps = setup.totalSteps;
        this.progressPercentage = setup.progressPercentage;

        this.timezones = this.loadTimezones();

        // The browser already knows where they are; guess from it so the common
        // case is one click. They can still change both.
        const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserZone && this.timezones.includes(browserZone)) {
            this.timezone = browserZone;
        }

        const guessed = this.guessCountryFromLocale();
        if (guessed) this.countryIso = guessed.iso;

        this.isLoading = false;
    }

    get selectedCountry(): Country | undefined {
        return countryByIso(this.countryIso);
    }

    /**
     * Every IANA zone the runtime knows, falling back to the browser's own zone
     * on older engines that do not expose supportedValuesOf.
     */
    private loadTimezones(): string[] {
        const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
        try {
            const zones = intl.supportedValuesOf?.('timeZone');
            if (zones?.length) return zones;
        } catch {
            // Fall through to the single-zone fallback below.
        }
        const own = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return own ? [own] : [];
    }

    /** Derive a country from the browser locale, e.g. "en-ZA" -> ZA. */
    private guessCountryFromLocale(): Country | undefined {
        const region = new Intl.Locale(navigator.language || 'en').maximize().region;
        return region ? countryByIso(region) : undefined;
    }

    async save(): Promise<void> {
        if (!this.selectedCountry) {
            await Swal.fire({ icon: 'warning', title: 'Choose your country' });
            return;
        }
        if (!this.organizationId) {
            await Swal.fire({ icon: 'error', title: 'No organization found', text: 'Please sign in again.' });
            return;
        }

        this.isSaving = true;

        try {
            // Stored as ISO alpha-2 and an IANA zone, matching exactly what
            // Planning Center writes, so every consumer treats both kinds of
            // organisation identically.
            await runInInjectionContext(this.injector, () =>
                updateDoc(doc(this.firestore, 'organizations', this.organizationId), {
                    countryCode: this.selectedCountry!.iso,
                    timezone: this.timezone || null,
                    updatedAt: new Date()
                })
            );

            await this.onboardingService.completeStep(this.organizationId, 'location-setup');

            const next = await this.onboardingService.resolveNextStep(this.organizationId);
            await this.router.navigate([next ? next.route : '/']);
        } catch (error) {
            console.error('Error saving organization location:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Could not save',
                text: (error as { message?: string })?.message || 'Please try again.'
            });
        } finally {
            this.isSaving = false;
        }
    }
}
